/**
 * Training enrollment tabs + in-page date selection for the registration form.
 */
document.addEventListener('DOMContentLoaded', () => {
	const enrollment = document.querySelector('.training-enrollment');
	const registerPanel = document.querySelector('#register');
	const dateTriggers = document.querySelectorAll('.js-training-register');
	const tabButtons = document.querySelectorAll('.js-training-enrollment-tab-button');
	const tabLinks = document.querySelectorAll('.js-training-enrollment-tab');

	if (!enrollment && !registerPanel) {
		return;
	}

	const getTab = (name) => {
		return enrollment
			? enrollment.querySelector('[data-training-panel="' + name + '"]')
			: document.querySelector('#' + name);
	};

	const activateTab = (tabName, { scroll = false, updateHistory = true } = {}) => {
		if (!tabName) {
			return;
		}

		const panels = enrollment ? enrollment.querySelectorAll('[data-training-panel]') : [];
		const buttons = enrollment ? enrollment.querySelectorAll('.js-training-enrollment-tab-button') : [];

		if (panels.length) {
			panels.forEach((panel) => {
				const isActive = panel.dataset.trainingPanel === tabName;
				panel.hidden = !isActive;
			});
		}

		if (buttons.length) {
			buttons.forEach((button) => {
				const isActive = button.dataset.trainingTab === tabName;
				button.setAttribute('aria-selected', isActive ? 'true' : 'false');
				button.setAttribute('tabindex', isActive ? '0' : '-1');
				button.classList.toggle('btn-primary', isActive);
				button.classList.toggle('outlined', !isActive);
			});
		}

		if (scroll) {
			const target = enrollment || getTab(tabName);
			if (target) {
				target.scrollIntoView({ behavior: 'smooth', block: 'start' });
			}
		}

		if (updateHistory && window.history && window.history.replaceState) {
			const url = new URL(window.location.href);
			url.hash = tabName;
			window.history.replaceState({}, '', url.toString());
		}
	};

	const findDateField = (dateId) => {
		if (!registerPanel) {
			return null;
		}

		const selects = registerPanel.querySelectorAll('select');
		for (const select of selects) {
			const option = Array.from(select.options).find((item) => item.value === dateId);
			if (option) {
				return select;
			}
		}

		const radios = registerPanel.querySelectorAll('input[type="radio"]');
		for (const radio of radios) {
			if (radio.value === dateId) {
				return radio;
			}
		}

		return null;
	};

	const getCapacityMap = () => {
		if (!registerPanel) {
			return {};
		}

		try {
			return JSON.parse(registerPanel.dataset.trainingCapacity || '{}');
		} catch (error) {
			return {};
		}
	};

	const getPersonsFieldWrap = () => {
		if (!registerPanel) {
			return null;
		}

		return registerPanel.querySelector('.strl-training-persons-field');
	};

	const findPersonsField = () => {
		const wrap = getPersonsFieldWrap();
		if (wrap) {
			const fromWrap =
				wrap.querySelector('select') ||
				wrap.querySelector('input[type="number"]') ||
				wrap.querySelector('input.ginput_quantity') ||
				wrap.querySelector('input');
			if (fromWrap) {
				return fromWrap;
			}
		}

		if (!registerPanel) {
			return null;
		}

		return (
			registerPanel.querySelector('select.strl-training-persons-field') ||
			registerPanel.querySelector(
				'input[type="number"][name^="input_"], input.ginput_quantity, .gfield--type-number input, .gfield--type-quantity input'
			)
		);
	};

	const getSelectedDateId = () => {
		if (!registerPanel) {
			return '';
		}

		const capacityKeys = Object.keys(getCapacityMap());
		if (!capacityKeys.length) {
			return '';
		}

		const dateWrap = registerPanel.querySelector('.strl-training-date-field');
		if (dateWrap) {
			const checkedRadio = dateWrap.querySelector('input[type="radio"]:checked');
			if (checkedRadio && checkedRadio.value && capacityKeys.includes(String(checkedRadio.value))) {
				return String(checkedRadio.value);
			}

			const select = dateWrap.querySelector('select');
			if (select && select.value && capacityKeys.includes(String(select.value))) {
				return String(select.value);
			}
		}

		const personsWrap = getPersonsFieldWrap();

		const radios = registerPanel.querySelectorAll('input[type="radio"][name^="input_"]');
		for (const radio of radios) {
			if (personsWrap && personsWrap.contains(radio)) {
				continue;
			}
			if (radio.checked && radio.value && capacityKeys.includes(String(radio.value))) {
				return String(radio.value);
			}
		}

		const selects = registerPanel.querySelectorAll('select[name^="input_"]');
		for (const select of selects) {
			if (personsWrap && personsWrap.contains(select)) {
				continue;
			}
			if (!select.value) {
				continue;
			}

			const value = String(select.value);
			if (capacityKeys.includes(value)) {
				return value;
			}
		}

		return '';
	};

	const getEffectiveRemaining = () => {
		const dateId = getSelectedDateId();
		const capacity = getCapacityMap();

		if (dateId && Object.prototype.hasOwnProperty.call(capacity, dateId)) {
			const remaining = capacity[dateId];
			if (null === remaining || undefined === remaining) {
				return null;
			}

			return parseInt(remaining, 10);
		}

		const values = Object.values(capacity)
			.filter((value) => null !== value && undefined !== value)
			.map((value) => parseInt(value, 10))
			.filter((value) => !Number.isNaN(value) && value >= 0);

		if (!values.length) {
			return null;
		}

		return Math.min(...values);
	};

	const setPersonsValidationMessage = (message) => {
		const wrap = getPersonsFieldWrap();
		if (!wrap) {
			return;
		}

		let validation = wrap.querySelector('.validation_message.strl-capacity-validation');
		if (!message) {
			if (validation) {
				validation.remove();
			}
			wrap.classList.remove('gfield_error');
			return;
		}

		if (!validation) {
			validation = document.createElement('div');
			validation.className =
				'gfield_description validation_message gfield_validation_message strl-capacity-validation';
			wrap.appendChild(validation);
		}

		validation.textContent = message;
		wrap.classList.add('gfield_error');
	};

	const updatePersonsFieldDescription = (remaining) => {
		const personsWrap = getPersonsFieldWrap();
		if (!personsWrap) {
			return;
		}

		let description = personsWrap.querySelector('.gfield_description:not(.validation_message)');
		if (null === remaining || Number.isNaN(remaining)) {
			if (description && description.dataset.strlCapacityDesc === 'true') {
				description.remove();
			}
			return;
		}

		const template =
			registerPanel.dataset.capacityMessage || 'Maximum %d place(s) remaining for the selected date.';
		const message = template.replace('%d', String(remaining));

		if (!description) {
			description = document.createElement('div');
			description.className = 'gfield_description';
			description.dataset.strlCapacityDesc = 'true';
			personsWrap.appendChild(description);
		}

		description.textContent = message;
		description.dataset.strlCapacityDesc = 'true';
	};

	const getCapacityCeiling = () => {
		const capacity = getCapacityMap();
		const values = Object.values(capacity)
			.filter((value) => null !== value && undefined !== value)
			.map((value) => parseInt(value, 10))
			.filter((value) => !Number.isNaN(value) && value >= 0);

		if (!values.length) {
			return 0;
		}

		return Math.max(...values);
	};

	const ensurePersonsSelectOptions = (personsField, ceiling) => {
		if (!personsField || 'SELECT' !== personsField.tagName || ceiling < 1) {
			return;
		}

		const existing = {};
		Array.from(personsField.options).forEach((option) => {
			if ('' !== option.value) {
				existing[String(option.value)] = option;
			}
		});

		for (let i = 1; i <= ceiling; i++) {
			const key = String(i);
			if (existing[key]) {
				continue;
			}

			const option = document.createElement('option');
			option.value = key;
			option.textContent = key;
			personsField.appendChild(option);
		}
	};

	const limitPersonsSelectOptions = (personsField, remaining) => {
		if (!personsField || 'SELECT' !== personsField.tagName) {
			return;
		}

		ensurePersonsSelectOptions(personsField, Math.max(remaining, getCapacityCeiling()));

		Array.from(personsField.options).forEach((option) => {
			if ('' === option.value) {
				option.disabled = false;
				option.hidden = false;
				return;
			}

			const value = parseInt(option.value, 10);
			const overLimit = !Number.isNaN(value) && value > remaining;
			option.disabled = overLimit;
			option.hidden = overLimit;
		});
	};

	const applyPersonsCapacityLimit = () => {
		const personsField = findPersonsField();
		if (!personsField) {
			return;
		}

		const remaining = getEffectiveRemaining();
		const ceiling = getCapacityCeiling();
		updatePersonsFieldDescription(remaining);

		if ('SELECT' === personsField.tagName && ceiling > 0) {
			ensurePersonsSelectOptions(personsField, ceiling);
		}

		if (null === remaining || Number.isNaN(remaining) || remaining < 1) {
			personsField.removeAttribute('max');
			delete personsField.dataset.strlMax;
			if ('SELECT' === personsField.tagName) {
				Array.from(personsField.options).forEach((option) => {
					option.disabled = false;
					option.hidden = false;
				});
			}
			setPersonsValidationMessage('');
			syncParticipantRequiredState();
			return;
		}

		personsField.setAttribute('max', String(remaining));
		personsField.dataset.strlMax = String(remaining);
		limitPersonsSelectOptions(personsField, remaining);

		const current = parseInt(personsField.value, 10) || 0;
		if (current > remaining) {
			personsField.value = String(remaining);
			personsField.dispatchEvent(new Event('input', { bubbles: true }));
			personsField.dispatchEvent(new Event('change', { bubbles: true }));
		}

		setPersonsValidationMessage('');
		syncParticipantRequiredState();
	};

	const clampPersonsInput = () => {
		const personsField = findPersonsField();
		if (!personsField) {
			return;
		}

		const max = parseInt(personsField.dataset.strlMax, 10);
		if (Number.isNaN(max) || max < 1) {
			setPersonsValidationMessage('');
			syncParticipantRequiredState();
			return;
		}

		let current = parseInt(personsField.value, 10);
		if (Number.isNaN(current) || current < 1) {
			setPersonsValidationMessage('');
			syncParticipantRequiredState();
			return;
		}

		if (current > max) {
			personsField.value = String(max);
			const template =
				registerPanel.dataset.capacityMessage || 'Maximum %d place(s) remaining for the selected date.';
			setPersonsValidationMessage(template.replace('%d', String(max)));
		} else {
			setPersonsValidationMessage('');
		}

		syncParticipantRequiredState();
	};

	const personsExceedsCapacity = () => {
		const personsField = findPersonsField();
		if (!personsField) {
			return false;
		}

		const remaining = getEffectiveRemaining();
		if (null === remaining || Number.isNaN(remaining)) {
			return false;
		}

		const persons = parseInt(personsField.value, 10) || 0;
		return persons > remaining;
	};

	const validatePersonsCapacityBeforeContinue = (event) => {
		applyPersonsCapacityLimit();

		if (!personsExceedsCapacity()) {
			setPersonsValidationMessage('');
			return;
		}

		const remaining = getEffectiveRemaining();
		const personsField = findPersonsField();
		if (personsField && null !== remaining) {
			personsField.value = String(remaining);
			personsField.dispatchEvent(new Event('input', { bubbles: true }));
			personsField.dispatchEvent(new Event('change', { bubbles: true }));
		}

		const template =
			registerPanel.dataset.capacityMessage || 'Maximum %d place(s) remaining for the selected date.';
		setPersonsValidationMessage(template.replace('%d', String(remaining)));

		if (event) {
			event.preventDefault();
			event.stopImmediatePropagation();
		}

		if (personsField && personsField.focus) {
			personsField.focus();
		}
	};

	const getParticipantNumber = (fieldEl) => {
		const match = (fieldEl.className || '').match(/(?:^|\s)strl-training-participant-(\d+)(?:\s|$)/);
		return match ? parseInt(match[1], 10) : 0;
	};

	const syncParticipantRequiredState = () => {
		if (!registerPanel) {
			return;
		}

		const personsField = findPersonsField();
		const persons = personsField ? parseInt(personsField.value, 10) || 0 : 0;
		const participantFields = registerPanel.querySelectorAll('.strl-training-participant-field');

		participantFields.forEach((fieldEl) => {
			const number = getParticipantNumber(fieldEl);
			if (!number) {
				return;
			}

			const isNeeded = number <= persons;
			const inputs = fieldEl.querySelectorAll('input, select, textarea');

			inputs.forEach((input) => {
				if (isNeeded) {
					input.setAttribute('aria-required', 'true');
					if (input.dataset.strlWasRequired === 'true') {
						input.required = true;
					}
				} else {
					if (input.required) {
						input.dataset.strlWasRequired = 'true';
					}
					input.required = false;
					input.removeAttribute('required');
					input.setAttribute('aria-required', 'false');
				}
			});
		});
	};

	const bindCapacityAndParticipantSync = () => {
		if (!registerPanel) {
			return;
		}

		applyPersonsCapacityLimit();

		const personsField = findPersonsField();
		if (personsField && !personsField.dataset.strlCapacityBound) {
			personsField.dataset.strlCapacityBound = 'true';
			personsField.addEventListener('input', clampPersonsInput);
			personsField.addEventListener('change', clampPersonsInput);
			personsField.addEventListener('keyup', clampPersonsInput);
			personsField.addEventListener('blur', clampPersonsInput);
		}

		if (!registerPanel.dataset.strlDateCapacityBound) {
			registerPanel.dataset.strlDateCapacityBound = 'true';
			registerPanel.addEventListener('change', (event) => {
				const target = event.target;
				if (!target) {
					return;
				}

				const personsWrap = getPersonsFieldWrap();
				if (personsWrap && personsWrap.contains(target)) {
					return;
				}

				if (target.matches('select[name^="input_"]') || target.matches('input[type="radio"][name^="input_"]')) {
					applyPersonsCapacityLimit();
				}
			});

			registerPanel.addEventListener(
				'click',
				(event) => {
					const target = event.target;
					if (!target || !target.closest) {
						return;
					}

					const nextOrSubmit = target.closest(
						'.gform_next_button, .gform_button[type="submit"], input[type="submit"], button[type="submit"]'
					);
					if (!nextOrSubmit) {
						return;
					}

					validatePersonsCapacityBeforeContinue(event);
				},
				true
			);
		}
	};

	const selectTrainingDate = (dateId, remaining) => {
		if (!dateId || !registerPanel) {
			return false;
		}

		const field = findDateField(dateId);
		if (!field) {
			return false;
		}

		if ('SELECT' === field.tagName) {
			field.value = dateId;
			field.dispatchEvent(new Event('change', { bubbles: true }));
		} else if ('radio' === field.type) {
			field.checked = true;
			field.dispatchEvent(new Event('change', { bubbles: true }));
			field.dispatchEvent(new Event('click', { bubbles: true }));
		}

		if ('' !== remaining && null !== remaining && undefined !== remaining) {
			const personsField = findPersonsField();
			const remainingNumber = parseInt(remaining, 10);
			if (personsField && !Number.isNaN(remainingNumber) && remainingNumber > 0) {
				personsField.setAttribute('max', String(remainingNumber));
				personsField.dataset.strlMax = String(remainingNumber);
				if (personsField.value && parseInt(personsField.value, 10) > remainingNumber) {
					personsField.value = String(remainingNumber);
				}
			}
		}

		applyPersonsCapacityLimit();

		return true;
	};

	const openRegisterWithDate = (dateId, remaining) => {
		activateTab('register', { scroll: true, updateHistory: true });
		selectTrainingDate(dateId, remaining);

		if (dateId && window.history && window.history.replaceState) {
			const url = new URL(window.location.href);
			url.searchParams.set('training_date_id', dateId);
			url.hash = 'register';
			window.history.replaceState({}, '', url.toString());
		}
	};

	tabButtons.forEach((button) => {
		button.addEventListener('click', () => {
			activateTab(button.dataset.trainingTab, { scroll: false, updateHistory: true });
		});

		button.addEventListener('keydown', (event) => {
			if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) {
				return;
			}

			event.preventDefault();
			const list = Array.from(tabButtons);
			const index = list.indexOf(button);
			let nextIndex = index;

			if ('ArrowRight' === event.key) {
				nextIndex = (index + 1) % list.length;
			} else if ('ArrowLeft' === event.key) {
				nextIndex = (index - 1 + list.length) % list.length;
			} else if ('Home' === event.key) {
				nextIndex = 0;
			} else if ('End' === event.key) {
				nextIndex = list.length - 1;
			}

			list[nextIndex].focus();
			activateTab(list[nextIndex].dataset.trainingTab, {
				scroll: false,
				updateHistory: true,
			});
		});
	});

	tabLinks.forEach((link) => {
		link.addEventListener('click', (event) => {
			const tabName = link.dataset.trainingTab || (link.getAttribute('href') || '').replace('#', '');
			if (!tabName || !getTab(tabName)) {
				return;
			}

			event.preventDefault();
			activateTab(tabName, { scroll: true, updateHistory: true });
		});
	});

	dateTriggers.forEach((trigger) => {
		trigger.addEventListener('click', (event) => {
			const dateId = trigger.dataset.trainingDateId || '';
			event.preventDefault();
			openRegisterWithDate(dateId, trigger.dataset.trainingRemaining);
		});
	});

	const params = new URLSearchParams(window.location.search);
	const hash = (window.location.hash || '').replace('#', '');
	const preselectedDate = params.get('training_date_id');

	if (preselectedDate) {
		activateTab('register', { scroll: true, updateHistory: false });
		const applyPreselect = () => {
			selectTrainingDate(preselectedDate, null);
			bindCapacityAndParticipantSync();
		};
		applyPreselect();
		document.addEventListener('gform_post_render', applyPreselect);
	} else if ('register' === hash || 'incompany' === hash) {
		activateTab(hash, { scroll: true, updateHistory: false });
	} else if (enrollment && enrollment.dataset.defaultTab) {
		activateTab(enrollment.dataset.defaultTab, {
			scroll: false,
			updateHistory: false,
		});
	}

	bindCapacityAndParticipantSync();
	document.addEventListener('gform_post_render', bindCapacityAndParticipantSync);
});
